import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { View, Platform, Pressable, ScrollView, Share } from 'react-native';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { Text } from '@constants/Themed';
import styles from '@stylesheet';
import apis from '@api/api';
import moment from 'moment';
import { AntDesign, FontAwesome5, MaterialIcons, Feather } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { SizableText, XStack, YStack, Card, H2, H4, H5, Separator } from 'tamagui';
import { useQuery } from '@tanstack/react-query';
import htmlTemplate from '../MidLevel/DataScreen/TemplatesScreen/InvoiceTemplates/template/htmlTemplate';
import * as Print from 'expo-print';
import { shareAsync } from 'expo-sharing';
import { useNavigation } from '@react-navigation/native';

// Status Badge Component
const InvoiceStatusBadge = ({ status, theme }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'sent': return '#3B82F6'; // Blue
      case 'viewed': return '#F59E0B'; // Yellow
      case 'paid': return '#10B981'; // Green
      case 'draft': return '#6B7280'; // Gray
      case 'canceled': return '#EF4444'; // Red
      default: return '#6B7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'sent': return 'Sent';
      case 'viewed': return 'Viewed';
      case 'paid': return 'Paid';
      case 'draft': return 'Draft';
      case 'canceled': return 'Canceled';
      default: return status || 'Unknown';
    }
  };

  return (
    <View style={{
      backgroundColor: getStatusColor(status),
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 12,
      alignSelf: 'flex-start'
    }}>
      <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
        {getStatusLabel(status)}
      </Text>
    </View>
  );
};

const InvoiceDetailScreen = ({ route, navigation }) => {
  const { invoiceId } = route.params;
  const theme = themeSelector();
  const activeCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const invoiceRef = useRef(null);

  // Fetch invoice data
  const { data: invoiceData, refetch } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      const data = await apis.getInvoiceById(invoiceId);
      return data;
    },
    enabled: !!invoiceId,
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  // Mark invoice as viewed when loaded
  useEffect(() => {
    if (invoiceData && invoiceData.status === 'sent') {
      apis.markInvoiceAsViewed(invoiceId).catch(err => {
        console.error('Failed to mark invoice as viewed:', err);
      });
    }
  }, [invoiceData, invoiceId]);

  useEffect(() => {
    if (invoiceData) {
      setInvoice(invoiceData);
      setLoading(false);
    }
  }, [invoiceData]);

  // Generate HTML content for export/print
  const htmlContent = useMemo(() => {
    if (!invoice) return '';

    // Map invoice data to template format
    const templateInvoice = {
      invoice_template_name: invoice.templateId?.invoice_template_name || invoice.title || 'Invoice',
      invoice_logo: invoice.templateId?.invoice_logo || '',
      from_email: invoice.templateId?.email || '',
      address: invoice.templateId?.address || '',
      contact_information: invoice.templateId?.contact_information || {},
      country_code: invoice.templateId?.country_code || '',
      bankName: invoice.templateId?.bankName || '',
      bankSortcode: invoice.templateId?.bankSortcode || '',
      bankAccountNumber: invoice.templateId?.bankAccountNumber || '',
      bankIban: invoice.templateId?.bankIban || '',
      toCompanyName: invoice.client?.name || '',
      toAddress: invoice.client?.address || '',
      toName: invoice.client?.name || '',
      date: moment(invoice.issue_date).format('DD/MM/YYYY'),
      dueDate: moment(invoice.due_date).format('DD/MM/YYYY'),
      invoiceId: invoice.invoice_number,
      notes: invoice.notes || invoice.templateId?.extra_notes || '',
      items: invoice.items || [],
      currencyCode: invoice.currency || 'USD',
    };

    const invoiceSettings = {
      showLogo: true,
      showSignature: true,
      showBankDetails: true,
      showNotes: true,
      showFooter: true,
    };

    return htmlTemplate(
      templateInvoice,
      invoice.subtotal || 0,
      invoice.total || 0,
      invoice.tax || 0,
      activeCompany,
      invoiceSettings
    );
  }, [invoice, activeCompany]);

  // Print/Export function
  const printToFile = useCallback(async () => {
    if (!htmlContent) return;

    if (Platform.OS === 'web') {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(htmlContent);
        printWindow.document.close();

        printWindow.onload = () => {
          const images = printWindow.document.images;
          let loadedCount = 0;
          const total = images.length;

          if (total === 0) {
            printWindow.focus();
            printWindow.print();
            return;
          }

          for (let i = 0; i < total; i++) {
            if (images[i].complete) {
              loadedCount++;
            } else {
              images[i].onload = () => {
                loadedCount++;
                if (loadedCount === total) {
                  printWindow.focus();
                  printWindow.print();
                }
              };
              images[i].onerror = () => {
                loadedCount++;
                if (loadedCount === total) {
                  printWindow.focus();
                  printWindow.print();
                }
              };
            }
          }

          if (loadedCount === total) {
            printWindow.focus();
            printWindow.print();
          }
        };
      }
    } else {
      try {
        const { uri } = await Print.printToFileAsync({ html: htmlContent });
        await shareAsync(uri, { UTI: '.pdf', mimeType: 'application/pdf' });
      } catch (error) {
        console.error('Error printing invoice:', error);
      }
    }
  }, [htmlContent]);

  if (loading) {
    return (
      <DefaultScreen backButton scrollDisabled>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Text>Loading invoice...</Text>
        </View>
      </DefaultScreen>
    );
  }

  if (!invoice) {
    return (
      <DefaultScreen backButton scrollDisabled>
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
          <Text>Invoice not found</Text>
        </View>
      </DefaultScreen>
    );
  }

  return (
    <DefaultScreen
      backButton
      scrollDisabled
      containerStyle={{ paddingBottom: 50 }}
      searchBar={{
        showSearch: false,
        customStyle: { top: 20 },
        otherComponents: [
          {
            function: printToFile,
            functionName: 'export',
            icon: <Feather name="download" size={18} color="white" />
          },
          {
            function: printToFile,
            functionName: 'print',
            icon: <FontAwesome5 name="print" size={18} color="white" />
          },
        ]
      }}
    >
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, paddingBottom: 100 }}>
        <YStack gap="$4">
          {/* Header Card */}
          <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" borderRadius={16}>
            <XStack alignItems="center" justifyContent="space-between" marginBottom="$3">
              <YStack flex={1}>
                <H2 color={theme ? '#1F2937' : '#F9FAFB'} marginBottom="$2">
                  Invoice #{invoice.invoice_number}
                </H2>
                <XStack alignItems="center" gap="$2" marginBottom="$2">
                  <InvoiceStatusBadge status={invoice.status} theme={theme} />
                  {invoice.viewed && (
                    <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'}>
                      Viewed {moment(invoice.viewedAt).fromNow()}
                    </SizableText>
                  )}
                </XStack>
              </YStack>
            </XStack>

            <Separator marginVertical="$3" />

            <XStack flexWrap="wrap" gap="$4">
              <YStack flex={1} minWidth={150}>
                <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} marginBottom="$1">
                  Issue Date
                </SizableText>
                <SizableText size="$3" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {moment(invoice.issue_date).format('MMM DD, YYYY')}
                </SizableText>
              </YStack>
              <YStack flex={1} minWidth={150}>
                <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} marginBottom="$1">
                  Due Date
                </SizableText>
                <SizableText size="$3" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {moment(invoice.due_date).format('MMM DD, YYYY')}
                </SizableText>
              </YStack>
              <YStack flex={1} minWidth={150}>
                <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} marginBottom="$1">
                  Total Amount
                </SizableText>
                <SizableText size="$3" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {invoice.currency} {invoice.total?.toFixed(2) || '0.00'}
                </SizableText>
              </YStack>
            </XStack>
          </Card>

          {/* Client Information */}
          <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" borderRadius={16}>
            <H4 marginBottom="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
              Bill To
            </H4>
            <YStack gap="$2">
              <YStack>
                <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} marginBottom="$1">
                  Client Name
                </SizableText>
                <SizableText size="$3" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {invoice.client?.name || 'N/A'}
                </SizableText>
              </YStack>
              <YStack>
                <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} marginBottom="$1">
                  Address
                </SizableText>
                <SizableText size="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {invoice.client?.address || 'N/A'}
                </SizableText>
              </YStack>
              <YStack>
                <SizableText size="$2" color={theme ? '#6B7280' : '#9CA3AF'} marginBottom="$1">
                  Email
                </SizableText>
                <SizableText size="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {invoice.client?.email || 'N/A'}
                </SizableText>
              </YStack>
            </YStack>
          </Card>

          {/* Invoice Items */}
          <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" borderRadius={16}>
            <H4 marginBottom="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
              Items
            </H4>
            <View style={{
              borderWidth: 1,
              borderColor: theme ? '#E5E7EB' : '#4B5563',
              borderRadius: 8,
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              <XStack
                backgroundColor={theme ? '#F9FAFB' : '#4B5563'}
                padding="$3"
                borderBottomWidth={1}
                borderBottomColor={theme ? '#E5E7EB' : '#6B7280'}
              >
                <SizableText flex={2} size="$3" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                  Description
                </SizableText>
                <SizableText flex={1} size="$3" fontWeight="600" textAlign="center" color={theme ? '#1F2937' : '#F9FAFB'}>
                  Quantity
                </SizableText>
                <SizableText flex={1} size="$3" fontWeight="600" textAlign="right" color={theme ? '#1F2937' : '#F9FAFB'}>
                  Unit Price
                </SizableText>
                <SizableText flex={1} size="$3" fontWeight="600" textAlign="right" color={theme ? '#1F2937' : '#F9FAFB'}>
                  Total
                </SizableText>
              </XStack>

              {/* Table Rows */}
              {invoice.items?.map((item, index) => {
                const itemTotal = (item.quantity || 0) * (item.unitPrice || 0);
                return (
                  <XStack
                    key={index}
                    padding="$3"
                    borderBottomWidth={index < invoice.items.length - 1 ? 1 : 0}
                    borderBottomColor={theme ? '#E5E7EB' : '#4B5563'}
                    backgroundColor={index % 2 === 0 ? (theme ? '#FFFFFF' : '#374151') : (theme ? '#F9FAFB' : '#4B5563')}
                  >
                    <SizableText flex={2} size="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
                      {item.description || 'N/A'}
                    </SizableText>
                    <SizableText flex={1} size="$3" textAlign="center" color={theme ? '#1F2937' : '#F9FAFB'}>
                      {item.quantity || 0}
                    </SizableText>
                    <SizableText flex={1} size="$3" textAlign="right" color={theme ? '#1F2937' : '#F9FAFB'}>
                      {invoice.currency} {item.unitPrice?.toFixed(2) || '0.00'}
                    </SizableText>
                    <SizableText flex={1} size="$3" textAlign="right" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                      {invoice.currency} {itemTotal.toFixed(2)}
                    </SizableText>
                  </XStack>
                );
              })}
            </View>
          </Card>

          {/* Totals */}
          <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" borderRadius={16}>
            <H4 marginBottom="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
              Summary
            </H4>
            <YStack gap="$2">
              <XStack justifyContent="space-between" alignItems="center">
                <SizableText size="$3" color={theme ? '#6B7280' : '#9CA3AF'}>
                  Subtotal
                </SizableText>
                <SizableText size="$3" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {invoice.currency} {invoice.subtotal?.toFixed(2) || '0.00'}
                </SizableText>
              </XStack>
              <XStack justifyContent="space-between" alignItems="center">
                <SizableText size="$3" color={theme ? '#6B7280' : '#9CA3AF'}>
                  Tax
                </SizableText>
                <SizableText size="$3" fontWeight="600" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {invoice.currency} {invoice.tax?.toFixed(2) || '0.00'}
                </SizableText>
              </XStack>
              <Separator marginVertical="$2" />
              <XStack justifyContent="space-between" alignItems="center">
                <SizableText size="$4" fontWeight="700" color={theme ? '#1F2937' : '#F9FAFB'}>
                  Total
                </SizableText>
                <SizableText size="$4" fontWeight="700" color={theme ? '#1F2937' : '#F9FAFB'}>
                  {invoice.currency} {invoice.total?.toFixed(2) || '0.00'}
                </SizableText>
              </XStack>
            </YStack>
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card backgroundColor={theme ? '#ffffff' : '#374151'} padding="$4" borderRadius={16}>
              <H4 marginBottom="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
                Notes
              </H4>
              <SizableText size="$3" color={theme ? '#1F2937' : '#F9FAFB'}>
                {invoice.notes}
              </SizableText>
            </Card>
          )}
        </YStack>
      </ScrollView>
    </DefaultScreen>
  );
};

export default InvoiceDetailScreen;






